import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

const HeaderNotificationListener = () => {
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        let timer;
        const handleToast = (event) => {
            const { type, message, duration } = event.detail;
            setNotification({ type, message, duration, id: Date.now() });
            
            // Auto scroll to top to ensure the notification is visible
            window.scrollTo({ top: 0, behavior: 'smooth' });

            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                setNotification(null);
            }, duration);
        };

        window.addEventListener('CUSTOM_HEADER_TOAST', handleToast);
        return () => {
            window.removeEventListener('CUSTOM_HEADER_TOAST', handleToast);
            if (timer) clearTimeout(timer);
        };
    }, []);

    const handleClose = () => {
        setNotification(null);
    };

    // Remove early return so AnimatePresence is always mounted
    const getColors = (type) => {
        switch (type) {
            case 'success':
                return { leftBorder: '#037f0c', iconColor: '#037f0c', icon: <CheckCircle2 size={20} color="#037f0c" /> };
            case 'delete':
                return { leftBorder: '#d13212', iconColor: '#d13212', icon: <CheckCircle2 size={20} color="#d13212" /> };
            case 'error':
                return { leftBorder: '#d13212', iconColor: '#d13212', icon: <XCircle size={20} color="#d13212" /> };
            case 'warning':
                return { leftBorder: '#ff9900', iconColor: '#ff9900', icon: <AlertCircle size={20} color="#ff9900" /> };
            case 'info':
                return { leftBorder: '#0073bb', iconColor: '#0073bb', icon: <Info size={20} color="#0073bb" /> };
            default:
                return { leftBorder: '#037f0c', iconColor: '#037f0c', icon: <CheckCircle2 size={20} color="#037f0c" /> };
        }
    };

    const colors = notification ? getColors(notification.type) : getColors('success');

    return (
        <AnimatePresence>
            {notification && (
                <motion.div
                    key={notification.id}
                initial={{ opacity: 0, y: -30, scale: 0.9, boxShadow: "0px 0px 0px rgba(0,0,0,0)" }}
                animate={{ 
                    opacity: 1, 
                    y: 0, 
                    scale: 1,
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.15)"
                }}
                exit={{ 
                    opacity: 0, 
                    y: -20, 
                    scale: 0.95, 
                    boxShadow: "0px 0px 0px rgba(0,0,0,0)",
                    transition: { type: "tween", duration: 0.2, ease: "easeIn" }
                }}
                transition={{ 
                    type: "spring", 
                    stiffness: 400, 
                    damping: 15 
                }}
                className="d-flex align-items-center justify-content-between w-100 p-3 mb-4"
                style={{
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #eaeded',
                    borderRight: '1px solid #eaeded',
                    borderBottom: '1px solid #eaeded',
                    borderLeft: `4px solid ${colors.leftBorder}`,
                    borderRadius: '2px',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center justify-content-center">
                        {colors.icon}
                    </div>
                    <span className="fs-9" style={{ color: '#16191f', fontWeight: 500 }}>
                        {notification.message}
                    </span>
                </div>
                <button 
                    onClick={handleClose}
                    className="btn btn-link p-0 hover-opacity-100 flex-shrink-0 d-flex"
                    style={{ color: '#545b64', opacity: 0.8, zIndex: 10 }}
                >
                    <X size={18} />
                </button>
            </motion.div>
            )}
        </AnimatePresence>
    );
};

export default HeaderNotificationListener;
